import { useState } from 'react';
import styled from 'styled-components';
import { FiArrowLeft, FiCheck, FiChevronDown, FiChevronUp, FiClock, FiCoffee, FiDroplet, FiGrid, FiHeart, FiHome, FiMapPin, FiMaximize2, FiMessageCircle, FiShare2, FiSun, FiX } from 'react-icons/fi';
import logo from '../IMAGES/nidologo.png';

import { barriosPorDepartamento } from '../data/barriosMendoza';
import BarrioInfo from '../components/EstiloDeVidaInfo/BarrioInfo';
import BarrioLifestyle from '../components/EstiloDeVidaInfo/BarrioLifeStyle';
import { PropertyDetail } from './PropertyDetail';

const formatFallback = (value = '') => value.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());

const TOURIST_LABELS = {
  wifi:'Wi-Fi', ropa_cama:'Ropa de cama', toallas:'Toallas', cocina_equipada:'Cocina equipada', estacionamiento:'Estacionamiento', desayuno:'Desayuno', limpieza:'Limpieza incluida', recepcion_24h:'Recepción 24 h',
  estufa_lena:'Estufa a leña', chimenea:'Chimenea', aire_acondicionado:'Aire acondicionado', calefaccion:'Calefacción', pileta:'Pileta', churrasquera:'Churrasquera / parrilla', jardin:'Jardín', terraza:'Terraza', juegos_mesa:'Juegos de mesa', ping_pong:'Mesa de ping pong', tv_streaming:'TV / streaming', lavadora:'Lavadora', mascotas:'Mascotas bienvenidas',
  romantica:'Escapada romántica', familias:'Ideal para familias', aventura:'Aventura y montaña', vino_gastronomia:'Vino y gastronomía', bienestar:'Relax y bienestar', trabajo_remoto:'Trabajo remoto',
  single:'Cama individual', double:'Cama doble', bunk:'Cucheta', sofaSingle:'Sofá cama individual', sofaDouble:'Sofá cama doble'
};

const CANCELLATION_LABELS = { flexible:'Flexible', moderada:'Moderada', estricta:'Estricta' };

function NewPropertyFinalPreview({
  propertyData,
  onBack,
  onUpdate,
  onPublish,
}) {

  const photos = propertyData.images || [];
  const description = propertyData.description || '';
  const features = propertyData.features || {};
  const vacation = propertyData.vacation || {};
  const isVacation = propertyData.operation === 'alquiler_vacacional';
  const serviceLabels = (vacation.services || []).map(key => TOURIST_LABELS[key] || formatFallback(key));
  const experienceLabels = (vacation.experiences || []).map(key => TOURIST_LABELS[key] || formatFallback(key));
  const bedLabels = Object.entries(vacation.bedTypes || {})
    .filter(([, quantity]) => Number(quantity) > 0)
    .map(([key, quantity]) => `${quantity} ${TOURIST_LABELS[key] || formatFallback(key)}`);

  const [activeIndex, setActiveIndex] = useState(0);
  const [publicando, setPublicando] = useState(false);
  const [publishError, setPublishError] = useState('');
  const [acceptedPublishingConditions, setAcceptedPublishingConditions] = useState(false);
  const [showPublishingConditions, setShowPublishingConditions] = useState(false);
  const [allowWhatsapp, setAllowWhatsapp] = useState(Boolean(propertyData.contactPreferences?.whatsapp));

  const getImageSrc = (photo) =>
    typeof photo === 'string' ? photo : photo?.preview || '';

  const publicPreviewProperty = {
    ...propertyData,
    images: photos.map(getImageSrc).filter(Boolean),
    title: propertyData.title || `${formatFallback(propertyData.type || 'propiedad')} en ${propertyData.neighborhood || propertyData.department || 'Mendoza'}`,
    status: propertyData.status || 'disponible',
    currency: propertyData.currency || 'USD',
    isDemo: false,
    engagement: { favorites: 0, contacts: 0 },
    author: {
      role: 'PARTICULAR',
      allowWhatsapp: Boolean(propertyData.contactPreferences?.whatsapp),
      phone: propertyData.contactPreferences?.phone || 'vista-previa',
    },
  };

  const formatLabel = (str = '') =>
    str.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  const amenityIcon = (feature) => {
    if (['living', 'living_comedor', 'comedor', 'cocina', 'estudio', 'sala_juegos'].includes(feature)) return <FiHome />;
    if (feature === 'lavadero') return <FiDroplet />;
    if (['parrilla', 'churrasquera', 'quincho', 'quincho_cerrado'].includes(feature)) return <FiCoffee />;
    if (['jardin', 'patio', 'terraza', 'balcon', 'galeria_abierta'].includes(feature)) return <FiSun />;
    if (['apto_mascotas', 'mascotas'].includes(feature)) return <FiHeart />;
    if (['cochera', 'cochera_doble', 'baulera'].includes(feature)) return <FiGrid />;
    if (['pileta', 'pileta_climatizada'].includes(feature)) return <FiDroplet />;
    return <FiCheck />;
  };

  /* ===============================
     BARRIO
  =============================== */

  const department = propertyData.department;
  const neighborhoodId = propertyData.neighborhood;

  let barrio = null;

  if (
    department &&
    neighborhoodId &&
    barriosPorDepartamento[department]
  ) {
    barrio = barriosPorDepartamento[department].find(
      (b) => b.id === neighborhoodId
    );
  }

  const handlePublish = async () => {
    if (!acceptedPublishingConditions) {
      setPublishError('Para publicar, confirmá que el aviso es veraz, que estás autorizado/a a usar las imágenes y que el precio es real.');
      return;
    }
    setPublicando(true);
    setPublishError('');

    try {
      await onPublish();
    } catch (e) {
      console.error(e);
      setPublicando(false);
      setPublishError(
        e?.message || 'No pudimos publicar la propiedad. Revisá los datos e intentá nuevamente.'
      );
    }
  };

  return (
    <>
      {publicando && <PublicandoOverlay />}

      <Page>
        <Container>
          <Card>

            <PublicMobilePreview>
              <PropertyDetail previewProperty={publicPreviewProperty} previewMode />
            </PublicMobilePreview>

            <LegacyPreview>
            <MobilePreviewNote>ASÍ SE VERÁ TU PUBLICACIÓN</MobilePreviewNote>
            <MobileDetailPreview>
              <MobileDetailTopbar>
                <span><FiArrowLeft /> Volver</span>
                <div><button type="button" aria-label="Compartir"><FiShare2 /></button><button type="button" aria-label="Guardar"><FiHeart /></button><button type="button" aria-label="Cerrar"><FiX /></button></div>
              </MobileDetailTopbar>
              <MobileDetailGallery>
                <img src={getImageSrc(photos[activeIndex])} alt="Vista previa de la propiedad" />
                {photos.length > 1 && <b>{activeIndex + 1} / {photos.length}</b>}
              </MobileDetailGallery>
              <MobileDetailContent>
                <small>{formatLabel(propertyData.operation || 'venta')} · {formatLabel(propertyData.type || 'propiedad')} · Disponible</small>
                <h2>{propertyData.title || `${formatLabel(propertyData.type || 'propiedad')} en ${propertyData.neighborhood || propertyData.department || 'Mendoza'}`}</h2>
                <p><FiMapPin />{propertyData.address || [propertyData.neighborhood, propertyData.department].filter(Boolean).join(', ') || 'Ubicación a confirmar'}</p>
                <MobileDetailPrice>
                  <span>{propertyData.operation === 'alquiler_vacacional' ? 'POR NOCHE' : 'VALOR'}</span>
                  <strong>USD {propertyData.price}</strong>
                </MobileDetailPrice>
                <MobileDetailFacts>
                  {propertyData.bedrooms > 0 && <span><b>{propertyData.bedrooms}</b>Dormitorios</span>}
                  {propertyData.rooms > 0 && <span><b>{propertyData.rooms}</b>Ambientes</span>}
                  {propertyData.bathrooms > 0 && <span><b>{propertyData.bathrooms}</b>Baños</span>}
                  {propertyData.surfaceCovered > 0 && <span><b>{propertyData.surfaceCovered} m²</b>Cubiertos</span>}
                </MobileDetailFacts>
                {description && <MobilePublicSection><i>LA PROPIEDAD</i><h3>Un lugar para conocer en detalle</h3><p className="copy">{description}</p></MobilePublicSection>}
                <MobilePublicSection><i>INFORMACIÓN</i><h3>Características principales</h3><MobileDetailsGrid>
                  <MobileDetailItem><FiHome /><div><span>Tipo</span><b>{formatLabel(propertyData.type || 'propiedad')}</b></div></MobileDetailItem>
                  <MobileDetailItem><FiGrid /><div><span>Operación</span><b>{formatLabel(propertyData.operation || 'venta')}</b></div></MobileDetailItem>
                  {propertyData.rooms > 0 && <MobileDetailItem><FiHome /><div><span>Ambientes</span><b>{propertyData.rooms}</b></div></MobileDetailItem>}
                  {propertyData.bedrooms > 0 && <MobileDetailItem><FiHome /><div><span>Dormitorios</span><b>{propertyData.bedrooms}</b></div></MobileDetailItem>}
                  {propertyData.bathrooms > 0 && <MobileDetailItem><FiCheck /><div><span>Baños</span><b>{propertyData.bathrooms}</b></div></MobileDetailItem>}
                  {propertyData.surfaceTotal > 0 && <MobileDetailItem><FiMaximize2 /><div><span>Superficie total</span><b>{propertyData.surfaceTotal} m²</b></div></MobileDetailItem>}
                  {propertyData.surfaceCovered > 0 && <MobileDetailItem><FiMaximize2 /><div><span>Superficie cubierta</span><b>{propertyData.surfaceCovered} m²</b></div></MobileDetailItem>}
                  {propertyData.age > 0 && <MobileDetailItem><FiClock /><div><span>Antigüedad</span><b>{propertyData.age} años</b></div></MobileDetailItem>}
                  {propertyData.condition && <MobileDetailItem><FiCheck /><div><span>Estado</span><b>{formatLabel(propertyData.condition)}</b></div></MobileDetailItem>}
                  {propertyData.orientation && <MobileDetailItem><FiMapPin /><div><span>Orientación</span><b>{formatLabel(propertyData.orientation)}</b></div></MobileDetailItem>}
                </MobileDetailsGrid></MobilePublicSection>
                {(features.ambientes?.length || features.exteriores?.length || features.comodidades?.length || features.extras?.length) > 0 && <MobilePublicSection><i>COMODIDADES</i><h3>Todo lo que ofrece</h3><MobileAmenities>{[...(features.ambientes || []), ...(features.exteriores || []), ...(features.comodidades || []), ...(features.extras || [])].map((feature)=><span key={feature}>{amenityIcon(feature)}{formatLabel(feature)}</span>)}</MobileAmenities></MobilePublicSection>}
                {['alquiler','alquiler_temporal'].includes(propertyData.operation) && (propertyData.rental?.depositMonths > 0 || propertyData.rental?.guarantees?.length > 0 || propertyData.rental?.requirements) && <MobilePublicSection><i>CONDICIONES DEL ALQUILER</i><h3>Todo claro desde el comienzo</h3><MobileRentalConditions>{propertyData.rental?.depositMonths > 0 && <span><FiCheck />Depósito: {propertyData.rental.depositMonths} {propertyData.rental.depositMonths === 1 ? 'mes' : 'meses'}</span>}{propertyData.rental?.guarantees?.map((guarantee)=><span key={guarantee}><FiCheck />{formatLabel(guarantee)}</span>)}{propertyData.rental?.requirements && <p className="copy">{propertyData.rental.requirements}</p>}</MobileRentalConditions></MobilePublicSection>}
              </MobileDetailContent>
              <MobileDetailContact><div><span>VALOR</span><strong>USD {propertyData.price}</strong></div><button type="button"><FiMessageCircle /> Contactar</button></MobileDetailContact>
            </MobileDetailPreview>

            {/* HEADER */}

            <Header>

              <Title>{propertyData.title}</Title>

              <Meta>
                <span>{propertyData.operation || 'Venta'}</span>
                <Dot />
                <span>{propertyData.department}</span>

                {propertyData.neighborhood && (
                  <>
                    <Dot />
                    <span>{propertyData.neighborhood}</span>
                  </>
                )}
              </Meta>

              <Price>USD {propertyData.price}{propertyData.operation === 'alquiler_vacacional' && <small> / noche</small>}{['alquiler','alquiler_temporal'].includes(propertyData.operation) && <small> / mes</small>}</Price>

              <Specs>

                {propertyData.operation === 'alquiler_vacacional' && propertyData.vacation?.maximumGuests && <Spec>{propertyData.vacation.maximumGuests} huéspedes</Spec>}
                {propertyData.operation !== 'alquiler_vacacional' && propertyData.rooms && <Spec>{propertyData.rooms} amb</Spec>}
                {propertyData.bedrooms && <Spec>{propertyData.bedrooms} dorm</Spec>}
                {propertyData.operation === 'alquiler_vacacional' && propertyData.vacation?.beds && <Spec>{propertyData.vacation.beds} camas · {propertyData.vacation.sleepingPlaces || propertyData.vacation.maximumGuests} plazas</Spec>}
                {propertyData.bathrooms && <Spec>{propertyData.bathrooms} baños</Spec>}
                {propertyData.surfaceCovered && <Spec>{propertyData.surfaceCovered} m²</Spec>}

              </Specs>

            </Header>

            {/* GALERÍA + PANEL */}

            <Grid>

              <Gallery>

                {propertyData.video && (
                  <PreviewVideo>
                    <span>RECORRIDO EN VIDEO</span>
                    <video src={propertyData.video.preview || propertyData.video.url} controls playsInline />
                  </PreviewVideo>
                )}

                <ImageFrame>

                  {photos.length > 0 && (
                    <>
                      <img
                        src={getImageSrc(photos[activeIndex])}
                        alt="Foto de la propiedad"
                      />

                      <Nav>

                        <Arrow
                          onClick={() =>
                            setActiveIndex((i) => Math.max(i - 1, 0))
                          }
                          disabled={activeIndex === 0}
                        >
                          ‹
                        </Arrow>

                        <Arrow
                          onClick={() =>
                            setActiveIndex((i) =>
                              Math.min(i + 1, photos.length - 1)
                            )
                          }
                          disabled={activeIndex === photos.length - 1}
                        >
                          ›
                        </Arrow>

                      </Nav>

                      <Counter>
                        {activeIndex + 1} / {photos.length}
                      </Counter>
                    </>
                  )}

                </ImageFrame>

                <Thumbs>

                  {photos.map((p, i) => (
                    <Thumb
                      key={i}
                      active={i === activeIndex}
                      onClick={() => setActiveIndex(i)}
                    >
                      <img src={getImageSrc(p)} alt="" />
                    </Thumb>
                  ))}

                </Thumbs>

              </Gallery>

              {/* PANEL DERECHO */}

              <SidePanel>

                <PanelTitle>Datos del anuncio</PanelTitle>

                <Data>

                  <div>
                    <strong>Precio</strong>
                    <span>USD {propertyData.price}{propertyData.operation === 'alquiler_vacacional' ? ' por noche' : ['alquiler','alquiler_temporal'].includes(propertyData.operation) ? ' por mes' : ''}</span>
                  </div>

                  {propertyData.vacation?.maximumGuests && <div><strong>Huéspedes</strong><span>Hasta {propertyData.vacation.maximumGuests}</span></div>}
                  {isVacation && vacation.sleepingPlaces > 0 && <div><strong>Plazas para dormir</strong><span>{vacation.sleepingPlaces} plazas</span></div>}
                  {propertyData.vacation?.minimumNights && <div><strong>Estadía mínima</strong><span>{propertyData.vacation.minimumNights} noches</span></div>}
                  {propertyData.vacation?.checkIn && <div><strong>Check-in</strong><span>Desde las {propertyData.vacation.checkIn}</span></div>}
                  {isVacation && vacation.checkOut && <div><strong>Check-out</strong><span>Hasta las {vacation.checkOut}</span></div>}
                  {isVacation && vacation.cancellationPolicy && <div><strong>Cancelación</strong><span>{CANCELLATION_LABELS[vacation.cancellationPolicy] || formatLabel(vacation.cancellationPolicy)}</span></div>}

                  {propertyData.address && (
                    <div>
                      <strong>Dirección</strong>
                      <span>{propertyData.address}</span>
                    </div>
                  )}

                  {propertyData.operation !== 'alquiler_vacacional' && propertyData.surfaceCovered && (
                    <div>
                      <strong>Sup. cubierta</strong>
                      <span>{propertyData.surfaceCovered} m²</span>
                    </div>
                  )}

                  {propertyData.operation !== 'alquiler_vacacional' && propertyData.surfaceTotal && (
                    <div>
                      <strong>Sup. total</strong>
                      <span>{propertyData.surfaceTotal} m²</span>
                    </div>
                  )}

                  {propertyData.operation !== 'alquiler_vacacional' && propertyData.rooms && (
                    <div>
                      <strong>Ambientes</strong>
                      <span>{propertyData.rooms}</span>
                    </div>
                  )}

                  {propertyData.bedrooms && (
                    <div>
                      <strong>Dormitorios</strong>
                      <span>{propertyData.bedrooms}</span>
                    </div>
                  )}

                  {propertyData.bathrooms && (
                    <div>
                      <strong>Baños</strong>
                      <span>{propertyData.bathrooms}</span>
                    </div>
                  )}

                  {propertyData.age && (
                    <div>
                      <strong>Antigüedad</strong>
                      <span>{propertyData.age} años</span>
                    </div>
                  )}

                  {propertyData.orientation && (
                    <div>
                      <strong>Orientación</strong>
                      <span>{propertyData.orientation}</span>
                    </div>
                  )}

                  {propertyData.condition && (
                    <div>
                      <strong>Condición</strong>
                      <span>{propertyData.condition}</span>
                    </div>
                  )}

                </Data>

                <ContactCard>

                  <h4>Contactar al anunciante</h4>

                  <p>
                    Los datos de contacto estarán disponibles una vez publicado
                    el anuncio.
                  </p>

                  <DisabledButton disabled>
                    Contactar
                  </DisabledButton>

                </ContactCard>

              </SidePanel>

            </Grid>

            {/* DESCRIPCIÓN */}

            {description && (

              <Section>

                <SectionTitle>Descripción</SectionTitle>

                <Description>
                  {description}
                </Description>

              </Section>

            )}

            {/* APTO CRÉDITO */}

            {propertyData.isAptoCredito && (

              <Section>

                <CreditoCard>

                  <CreditoText>

                    <strong>Apto crédito hipotecario</strong>

                    <span>
                      Esta propiedad puede financiarse con crédito bancario
                    </span>

                  </CreditoText>

                </CreditoCard>

              </Section>

            )}

            {/* FEATURES */}

            {features && (

              <Section>

                <SectionTitle>Características</SectionTitle>

                <Chips>

                  {features.ambientes?.map((f, i) => (
                    <Chip key={i}>{formatLabel(f)}</Chip>
                  ))}

                  {features.exteriores?.map((f, i) => (
                    <Chip key={i}>{formatLabel(f)}</Chip>
                  ))}

                  {features.comodidades?.map((f, i) => (
                    <Chip key={i}>{formatLabel(f)}</Chip>
                  ))}

                  {features.extras?.map((f, i) => (
                    <Chip key={i}>{formatLabel(f)}</Chip>
                  ))}

                </Chips>

              </Section>

            )}

            {isVacation && (serviceLabels.length > 0 || experienceLabels.length > 0 || bedLabels.length > 0 || vacation.houseRules) && (
              <TouristSummary>
                <TouristSummaryIntro><span>TU ALOJAMIENTO, DE UN VISTAZO</span><h2>Así se verá la estadía.</h2></TouristSummaryIntro>
                {bedLabels.length > 0 && <Section><SectionTitle>Distribución para dormir</SectionTitle><Chips>{bedLabels.map(label => <Chip key={label}>{label}</Chip>)}</Chips></Section>}
                {serviceLabels.length > 0 && <Section><SectionTitle>Servicios incluidos</SectionTitle><Chips>{serviceLabels.map(label => <Chip key={label}>{label}</Chip>)}</Chips></Section>}
                {experienceLabels.length > 0 && <Section><SectionTitle>Ideal para</SectionTitle><Chips>{experienceLabels.map(label => <Chip key={label}>{label}</Chip>)}</Chips></Section>}
                {vacation.houseRules && <Section><SectionTitle>Reglas de la casa</SectionTitle><Description>{vacation.houseRules}</Description></Section>}
              </TouristSummary>
            )}

            {/* BARRIO */}

            {barrio && (
              <>
                <BarrioInfo barrio={barrio} />
                <BarrioLifestyle barrio={barrio} />
              </>
            )}
            </LegacyPreview>

            {/* ACCIONES */}

            {publishError && (
              <PublishError role="alert">
                <strong>No se pudo publicar todavía</strong>
                <span>{publishError}</span>
              </PublishError>
            )}

            <PublishContactPreference>
              <input id="allow-whatsapp" type="checkbox" checked={allowWhatsapp} onChange={(event)=>{const whatsapp=event.target.checked;setAllowWhatsapp(whatsapp);onUpdate?.({contactPreferences:{...(propertyData.contactPreferences||{}),whatsapp}});}} />
              <label htmlFor="allow-whatsapp">Permitir consultas por WhatsApp</label>
              <span>Las personas interesadas podrán escribirte directamente.</span>
            </PublishContactPreference>

            <PublishingConditions>
              <input id="publishing-conditions" type="checkbox" checked={acceptedPublishingConditions} onChange={(event)=>{setAcceptedPublishingConditions(event.target.checked);if(event.target.checked)setPublishError('');}} />
              <label htmlFor="publishing-conditions">Acepto las condiciones de publicación.</label>
              <button type="button" onClick={()=>setShowPublishingConditions(value=>!value)} aria-expanded={showPublishingConditions}>Leer condiciones {showPublishingConditions ? <FiChevronUp /> : <FiChevronDown />}</button>
              {showPublishingConditions && <p>Declaro que el aviso es veraz y que estoy autorizado/a a usar estas fotos. El precio debe ser real y definitivo: Nido no admite publicaciones “a consultar”, valores simbólicos ni $0. Si detectamos imágenes, información o condiciones irregulares, la publicación será dada de baja.</p>}
            </PublishingConditions>

            <Actions>

              <Secondary type="button" onClick={onBack}>
                Atrás
              </Secondary>

              <Primary onClick={handlePublish} disabled={!acceptedPublishingConditions}>

                {publicando
                  ? 'Guardando…'
                  : propertyData?._id
                    ? 'Guardar cambios'
                    : 'Publicar propiedad'}

              </Primary>

            </Actions>

          </Card>
        </Container>
      </Page>
    </>
  );
}

function PublicandoOverlay() {
  return (
    <Overlay role="status" aria-live="polite" aria-label="Preparando la publicación">
      <Orb $one />
      <Orb $two />
      <OverlayContent>
        <BrandMark src={logo} alt="NIDO" />
        <OverlayEyebrow>Tu publicación está tomando forma</OverlayEyebrow>
        <OverlayTitle>Estamos preparando<br />tu propiedad.</OverlayTitle>
        <OverlayText>
          Ordenamos la información, cuidamos cada imagen y dejamos todo listo
          para que tu anuncio se vea impecable.
        </OverlayText>
        <BuildingCard aria-hidden="true">
          <BuildingImage><span /></BuildingImage>
          <BuildingCopy>
            <i />
            <i />
            <i />
          </BuildingCopy>
        </BuildingCard>
        <ProgressRail aria-hidden="true"><span /></ProgressRail>
        <LoadingSteps>
          <span>Ordenando información</span>
          <span>Cuidando imágenes</span>
          <span>Creando tu anuncio</span>
        </LoadingSteps>
      </OverlayContent>
    </Overlay>
  );
}

export default NewPropertyFinalPreview;

/* ===================== TODOS TUS STYLES ORIGINALES ===================== */
/* NO SE HA ELIMINADO NI MODIFICADO NADA */

/* ===================== STYLES ===================== */

const Page = styled.div`
  padding-top: 32px;
  background: #f3f0ea;
  min-height: 100vh;
  min-width:0;
  overflow-x:hidden;
  @media(max-width:600px){padding-top:22px;}
`;

const Container = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 24px 120px;
  @media(max-width:600px){padding:0 14px 76px;}
`;

const Card = styled.div`
  background: white;
  border-radius: 26px;
  padding: 32px;
  box-shadow: 0 28px 60px rgba(0, 0, 0, 0.08);
  min-width:0;
  @media(max-width:600px){padding:22px 16px;border-radius:21px;}
`;
const PublicMobilePreview=styled.div`display:none;@media(max-width:900px){display:block;margin:0 -32px;}@media(max-width:600px){margin:0 -16px;}`;
const LegacyPreview=styled.div`@media(max-width:900px){display:none;}`;
const MobilePreviewNote=styled.p`display:none;@media(max-width:900px){display:block;margin:0 0 10px;color:#b45f40;font-size:11px;font-weight:850;letter-spacing:.14em;}`;
const MobileDetailPreview=styled.article`display:none;@media(max-width:900px){display:block;overflow:hidden;margin:0 -32px;background:#fbfaf7;}@media(max-width:600px){margin:0 -16px;}`;
const MobileDetailTopbar=styled.header`display:none;@media(max-width:900px){display:flex;height:76px;align-items:center;justify-content:space-between;padding:0 24px;color:#29474f;background:#fbfaf7;font-size:15px;font-weight:760;span{display:inline-flex;align-items:center;gap:8px;}div{display:flex;gap:9px;}button{display:grid;width:43px;height:43px;place-items:center;padding:0;color:#29474f;background:#fff;border:1px solid #e0e5e3;border-radius:50%;font-size:18px;}}`;
const MobileDetailGallery=styled.div`display:none;@media(max-width:900px){position:relative;display:block;height:min(54dvh,410px);min-height:330px;background:#172b30;img{display:block;width:100%;height:100%;object-fit:cover;}b{position:absolute;right:14px;bottom:14px;padding:9px 14px;border:1px solid rgba(255,255,255,.24);border-radius:999px;color:#fff;background:rgba(30,48,52,.78);font-size:12px;backdrop-filter:blur(10px);}}`;
const MobileDetailContent=styled.div`display:none;@media(max-width:900px){display:block;padding:36px 38px 0;background:#fbfaf7;small{display:block;margin-bottom:20px;color:#b45f40;font-size:11px;font-weight:800;letter-spacing:.13em;text-transform:uppercase;}h2{margin:0 0 28px;color:#20373c;font-size:clamp(37px,10vw,56px);font-weight:540;line-height:1.04;letter-spacing:-.06em;}p{display:flex;align-items:flex-start;gap:9px;margin:0;color:#728084;font-size:17px;line-height:1.45;}p svg{flex:0 0 auto;margin-top:3px;color:#c66f4e;}}`;
const MobileDetailPrice=styled.div`display:grid;gap:8px;margin-top:48px;padding-bottom:32px;span{color:#819092;font-size:11px;font-weight:800;letter-spacing:.15em;}strong{color:#20373c;font-size:43px;font-weight:680;letter-spacing:-.06em;}`;
const MobileDetailFacts=styled.div`display:flex;overflow-x:auto;margin:0 -38px;border-top:1px solid rgba(42,64,70,.12);border-bottom:1px solid rgba(42,64,70,.12);scrollbar-width:none;span{display:grid;flex:1 0 112px;gap:7px;min-width:112px;padding:24px 18px;border-right:1px solid rgba(42,64,70,.1);color:#819092;font-size:11px;line-height:1.2;}b{color:#29474f;font-size:24px;font-weight:700;}`;
const MobilePublicSection=styled.section`display:none;@media(max-width:900px){display:block;padding:56px 0 0;i{display:block;margin-bottom:12px;color:#b45f40;font-size:11px;font-style:normal;font-weight:800;letter-spacing:.15em;}h3{margin:0 0 25px;color:#20373c;font-size:clamp(30px,7vw,42px);font-weight:540;line-height:1.08;letter-spacing:-.05em;}p.copy{display:block;margin:0;color:#596b70;font-size:17px;line-height:1.72;}}`;
const MobileDetailsGrid=styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));border-top:1px solid rgba(42,64,70,.13);border-left:1px solid rgba(42,64,70,.13);`;
const MobileDetailItem=styled.div`display:flex;align-items:center;gap:12px;min-height:105px;padding:16px 12px;border-right:1px solid rgba(42,64,70,.13);border-bottom:1px solid rgba(42,64,70,.13);svg{width:34px;height:34px;flex:0 0 auto;padding:8px;color:#b45f40;background:#f0e7df;border-radius:50%;box-sizing:border-box;}span,b{display:block;}span{margin-bottom:4px;color:#819092;font-size:12px;}b{color:#29474f;font-size:15px;line-height:1.25;}`;
const MobileAmenities=styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;span{display:flex;min-height:88px;flex-direction:column;justify-content:space-between;gap:10px;padding:14px;color:#29474f;background:#f4f2ec;border:1px solid rgba(42,64,70,.1);border-radius:15px;font-size:14px;font-weight:680;line-height:1.25;}svg{width:32px;height:32px;padding:8px;box-sizing:border-box;color:#b45f40;background:#fff;border-radius:11px;}`;
const MobileRentalConditions=styled.div`display:grid;gap:0;span{display:flex;align-items:center;gap:12px;padding:17px 0;color:#596b70;border-bottom:1px solid rgba(42,64,70,.12);font-size:16px;}span svg{color:#b45f40;}.copy{padding-top:14px;}`;
const MobileDetailContact=styled.div`display:none;@media(max-width:900px){display:flex;align-items:center;justify-content:space-between;gap:14px;padding:18px 28px;color:#29474f;background:#fff;border-top:1px solid rgba(42,64,70,.13);span,strong{display:block;}span{margin-bottom:5px;color:#819092;font-size:10px;font-weight:800;letter-spacing:.14em;}strong{font-size:24px;letter-spacing:-.05em;}button{display:inline-flex;align-items:center;gap:8px;padding:14px 20px;color:#fff;background:#b96242;border:0;border-radius:999px;font-size:16px;font-weight:760;white-space:nowrap;}}`;

const Header = styled.div`
  margin-bottom: 24px;
  @media(max-width:900px){display:none;}
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 6px;
  overflow-wrap:anywhere;
`;

const Meta = styled.div`
  display: flex;
  gap: 6px;
  font-size: 15px;
  color: #777;
  align-items:center;
  flex-wrap:wrap;
`;

const Dot = styled.span`
  width: 4px;
  height: 4px;
  background: #bbb;
  border-radius: 50%;
`;

const Price = styled.div`
  margin-top: 8px;
  font-size: 21px;
  font-weight: 600;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0,1.6fr) minmax(0,1fr);
  gap: 28px;
  margin-bottom: 36px;

  @media (max-width: 900px) {
    display:none;
  }
`;

const Gallery = styled.div`
  min-width: 0;
`;
const PreviewVideo = styled.div`
  margin-bottom:14px;padding:14px;background:#20373c;border-radius:18px;
  span{display:block;margin:0 0 10px;color:#efad90;font-size:12px;font-weight:800;letter-spacing:.15em;}
  video{display:block;width:100%;max-height:430px;background:#14282d;border-radius:12px;object-fit:contain;}
`;

const ImageFrame = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  background: #eee;
  border-radius: 18px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Nav = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
`;

const Arrow = styled.button`
  background: rgba(0, 0, 0, 0.45);
  color: white;
  border: none;
  font-size: 26px;
  padding: 6px 12px;
  border-radius: 12px;
  cursor: pointer;

  &:disabled {
    opacity: 0.3;
  }
`;

const Counter = styled.div`
  position: absolute;
  bottom: 10px;
  right: 12px;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  font-size: 14px;
  padding: 4px 10px;
  border-radius: 999px;
`;

const Thumbs = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  overflow-x: auto;
`;

const Thumb = styled.div`
  border-radius: 8px;
  overflow: hidden;
  border: ${({ active }) =>
    active ? '2px solid #C66F4E' : '2px solid transparent'};
  cursor: pointer;

  img {
    width: 60px;
    height: 44px;
    object-fit: cover;
  }
`;

const SidePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PanelTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
`;

const Data = styled.div`
  font-size: 15px;
  color: #555;

  div {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    gap:14px;
    min-width:0;
  }

  span { min-width:0;text-align:right;overflow-wrap:anywhere; }

  strong {
    color: #333;
  }
`;

const ContactCard = styled.div`
  background: #fafafa;
  border-radius: 18px;
  padding: 16px;

  h4 {
    margin: 0 0 6px;
    font-size: 16px;
    font-weight: 600;
  }

  p {
    font-size: 14px;
    color: #777;
    margin-bottom: 12px;
  }
`;

const DisabledButton = styled.button`
  width: 100%;
  padding: 10px;
  border-radius: 999px;
  border: none;
  background: #e0e0e0;
  font-size: 15px;
  cursor: not-allowed;
`;

const Section = styled.div`
  width:100%;
  max-width: 680px;
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 12px;
`;


const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.span`
  background: #f2f2f2;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 14px;
  color: #444;
`;

const Description = styled.p`
  font-size: 15.5px;
  line-height: 1.6;
  max-width: 680px;
  color: #444;
  overflow-wrap:anywhere;
`;

const TouristSummary = styled.div`
  display:grid;
  grid-template-columns:repeat(2,minmax(0,1fr));
  gap:14px;
  margin:8px 0 34px;
  padding:26px;
  border-radius:24px;
  background:linear-gradient(135deg,#f8eee7 0%,#eef5f1 100%);
  border:1px solid #e7ddd4;
  > ${Section}{margin:0;padding:18px;border-radius:18px;background:rgba(255,255,255,.82);box-shadow:0 8px 20px rgba(64,75,70,.05);}
  > ${Section}:nth-of-type(2){background:#f0f7f1;}
  > ${Section}:nth-of-type(3){background:#fff3e9;}
  > ${Section}:nth-of-type(4){grid-column:1/-1;background:#29474f;color:white;}
  > ${Section}:nth-of-type(4) ${Description}{color:#e4eeea;}
  ${SectionTitle}{margin-bottom:11px;color:#29474f;font-size:16px;}
  > ${Section}:nth-of-type(4) ${SectionTitle}{color:#ffd0bb;}
  ${Chip}{background:#fff;border:1px solid rgba(41,71,79,.11);color:#29474f;font-weight:650;}
  @media(max-width:620px){grid-template-columns:1fr;padding:17px;gap:11px;> ${Section}{padding:16px;}> ${Section}:nth-of-type(4){grid-column:auto;}}
`;

const TouristSummaryIntro = styled.header`
  grid-column:1/-1;
  margin:0 0 3px;
  span{display:block;color:#b45f40;font-size:11px;font-weight:850;letter-spacing:.14em;}
  h2{margin:5px 0 0;color:#29474f;font-size:clamp(24px,4vw,32px);letter-spacing:-.04em;}
  @media(max-width:620px){grid-column:auto;}
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap:14px;
  @media(max-width:900px){position:fixed;z-index:1100;right:0;bottom:0;left:0;display:grid;grid-template-columns:1fr 1.45fr;gap:10px;padding:13px 16px calc(13px + env(safe-area-inset-bottom));border-top:1px solid rgba(41,71,79,.1);background:rgba(255,253,249,.96);backdrop-filter:blur(14px);button{width:100%;min-height:52px;padding:0;border-radius:15px;font:inherit;font-size:.92rem;font-weight:850;}button:first-child{border:1px solid #d5dfdc;background:#fff;color:#51696e;box-shadow:none;}button:last-child{border:0;background:#c66f4e;color:#fff;box-shadow:0 9px 20px rgba(198,111,78,.2);}}
`;

const PublishError = styled.div`
  display: grid;
  gap: 5px;
  margin: 8px 0 22px;
  padding: 17px 19px;
  border: 1px solid rgba(166, 55, 40, .28);
  border-radius: 16px;
  background: #fff1ed;
  color: #8f3529;
  font-size: 16px;
  line-height: 1.5;

  strong { font-size: 17px; }
`;

const PublishingConditions=styled.div`
  display:flex;align-items:center;flex-wrap:wrap;gap:6px 11px;margin:0 0 22px;padding:13px 2px 16px;
  border:0;border-bottom:1px solid #dedfd9;border-radius:0;background:transparent;color:#29474f;
  input{flex:0 0 auto;width:20px;height:20px;margin:0;accent-color:#c66f4e;}
  label{cursor:pointer;font-size:15px;font-weight:680;}
  button{display:inline-flex;align-items:center;gap:4px;padding:0;color:#b45f40;background:transparent;border:0;font:inherit;font-size:13px;font-weight:760;text-decoration:underline;cursor:pointer;}
  button svg{font-size:15px;}
  p{flex:0 0 100%;margin:7px 0 0;padding:12px 14px;border-radius:10px;background:#fff5f0;color:#6f6460;font-size:13px;line-height:1.55;}
`;
const PublishContactPreference=styled.div`
  display:grid;grid-template-columns:20px 1fr;column-gap:11px;row-gap:2px;margin:16px 0 0;padding:0 2px 14px;border:0;border-bottom:1px solid #dedfd9;border-radius:0;background:transparent;color:#29474f;cursor:pointer;
  input{grid-row:1 / span 2;width:20px;height:20px;margin:1px 0 0;accent-color:#c66f4e;}
  label{font-size:15px;font-weight:720;cursor:pointer;}span{grid-column:2;color:#748185;font-size:13px;line-height:1.4;}
`;

const Primary = styled.button`
  background: linear-gradient(135deg, #C66F4E 0%, #ff9d5a 100%);
  color: white;
  padding: 16px 44px;
  border-radius: 999px;
  border: none;
  font-weight: 600;
  font-size: 17px;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 12px 28px rgba(198,111,78, 0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 38px rgba(198,111,78, 0.45);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 10px 20px rgba(198,111,78, 0.35);
  }
  &:disabled{opacity:.5;cursor:not-allowed;transform:none;box-shadow:none;}
`;

const Secondary = styled.button`
  background: none;
  border: none;
  font-size: 15px;
  color: #666;
  cursor: pointer;
`;

/* ===================== OVERLAY STYLES ===================== */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  background:
    radial-gradient(circle at 18% 18%, rgba(255,255,255,.13), transparent 28%),
    radial-gradient(circle at 82% 78%, rgba(41,71,79,.18), transparent 31%),
    linear-gradient(145deg, #c66543 0%, #d77955 52%, #bd5d40 100%);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OverlayContent = styled.div`
  position: relative;
  z-index: 2;
  width: min(760px, calc(100vw - 40px));
  padding: 42px 24px;
  text-align: center;
  color: white;
  @media(max-width:520px){padding:24px 12px;}
`;

const BrandMark = styled.img`
  width: clamp(112px, 10vw, 158px);
  height: auto;
  margin-bottom: 34px;
  filter: brightness(0) invert(1);
  animation: brandArrival .75s cubic-bezier(.2,.8,.2,1) both;

  @keyframes brandArrival {
    from { opacity: 0; transform: translateY(15px) scale(.94); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

const OverlayEyebrow = styled.p`
  margin: 0 0 14px;
  color: #ffe4d8;
  font-size: clamp(14px, 1.2vw, 17px);
  font-weight: 800;
  letter-spacing: .14em;
  text-transform: uppercase;
`;

const OverlayTitle = styled.h2`
  margin: 0 auto 22px;
  font-size: clamp(46px, 5.8vw, 78px);
  line-height: .94;
  letter-spacing: -.055em;
  font-weight: 650;
  @media(max-width:520px){font-size:clamp(38px,11vw,52px);}
`;

const OverlayText = styled.p`
  max-width: 620px;
  margin: 0 auto;
  font-size: clamp(18px, 1.55vw, 22px);
  line-height: 1.55;
  color: rgba(255,255,255,.9);
`;

const BuildingCard = styled.div`
  width: 190px;
  height: 128px;
  margin: 36px auto 28px;
  padding: 13px;
  display: grid;
  grid-template-columns: 44% 1fr;
  gap: 13px;
  border: 1px solid rgba(255,255,255,.32);
  border-radius: 22px;
  background: rgba(255,255,255,.16);
  box-shadow: 0 28px 70px rgba(82,40,26,.18);
  backdrop-filter: blur(9px);
  animation: cardFloat 2.8s ease-in-out infinite;
  box-sizing:border-box;

  @keyframes cardFloat {
    0%, 100% { transform: translateY(0) rotate(-1deg); }
    50% { transform: translateY(-8px) rotate(1deg); }
  }
`;

const BuildingImage = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 13px;
  background: linear-gradient(145deg, #ffe1d1, #f5aa85);

  &::before, &::after, span {
    content: '';
    position: absolute;
    background: rgba(255,255,255,.78);
  }
  &::before { width: 38px; height: 45px; left: 11px; bottom: 0; border-radius: 4px 4px 0 0; }
  &::after { width: 24px; height: 31px; right: 9px; bottom: 0; border-radius: 4px 4px 0 0; }
  span { width: 10px; height: 10px; left: 25px; bottom: 21px; background: #d77955; }
`;

const BuildingCopy = styled.div`
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 11px;

  i {
    display: block;
    height: 8px;
    border-radius: 99px;
    background: rgba(255,255,255,.72);
    animation: copyGlow 1.8s ease-in-out infinite;
  }
  i:nth-child(1) { width: 82%; }
  i:nth-child(2) { width: 100%; animation-delay: .2s; }
  i:nth-child(3) { width: 63%; animation-delay: .4s; }

  @keyframes copyGlow { 50% { opacity: .38; transform: scaleX(.92); transform-origin: left; } }
`;

const ProgressRail = styled.div`
  width: min(460px, 82vw);
  height: 5px;
  margin: 0 auto 17px;
  overflow: hidden;
  border-radius: 99px;
  background: rgba(255,255,255,.24);

  span {
    display: block;
    width: 42%;
    height: 100%;
    border-radius: inherit;
    background: #fff;
    animation: travel 1.7s cubic-bezier(.65,0,.35,1) infinite;
  }

  @keyframes travel {
    from { transform: translateX(-105%); }
    to { transform: translateX(345%); }
  }
`;

const LoadingSteps = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  color: rgba(255,255,255,.82);
  font-size: 15px;
  font-weight: 650;

  span::before { content: '•'; margin-right: 8px; color: #ffe2d4; }

  @media (max-width: 620px) {
    gap: 8px;
    flex-direction: column;
    font-size: 16px;
  }
`;

const Orb = styled.span`
  position: absolute;
  width: ${({ $one }) => ($one ? '440px' : '300px')};
  height: ${({ $one }) => ($one ? '440px' : '300px')};
  top: ${({ $one }) => ($one ? '-150px' : 'auto')};
  right: ${({ $one }) => ($one ? '-80px' : 'auto')};
  bottom: ${({ $two }) => ($two ? '-90px' : 'auto')};
  left: ${({ $two }) => ($two ? '-70px' : 'auto')};
  border: 1px solid rgba(255,255,255,.16);
  border-radius: 50%;
  animation: orbPulse 5s ease-in-out infinite;

  @keyframes orbPulse {
    50% { transform: scale(1.12); opacity: .55; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Specs = styled.div`
  display: flex;
  gap: 18px;
  margin-top: 10px;
  flex-wrap:wrap;
`;

const Spec = styled.div`
  font-size: 14px;
  color: #555;
  font-weight: 500;
`;

const CreditoCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 22px;
  border-radius: 14px;
  background: #e8f1ff;
  border: 1px solid #2f6fed;
`;

const CreditoText = styled.div`
  display: flex;
  flex-direction: column;

  strong {
    font-size: 15px;
  }

  span {
    font-size: 15px;
    color: #555;
  }
`;
